import {
  chebyshevToRect,
  DIRECTION_DELTAS,
  type Direction,
  type TilePos,
} from '@veriverse/game-logic';
import { getMapObjects, type MapObject, type TiledMap } from '@veriverse/map-format';
import { interactionFor } from '@veriverse/shared';

/** An object the local player can use, with what the prompt needs. */
export interface InteractiveObject {
  readonly object: MapObject;
  readonly range: number;
  readonly labelKey: string;
}

/** The map's objects whose type has an interaction descriptor (spec §6.6). */
export function interactiveObjects(map: TiledMap): InteractiveObject[] {
  return getMapObjects(map).flatMap((object) => {
    const descriptor = interactionFor(object.type);
    return descriptor ? [{ object, range: descriptor.range, labelKey: descriptor.labelKey }] : [];
  });
}

/**
 * The object the "E" prompt points at: the closest one in range. Ties go to the object the player
 * faces, then to the lowest object id so the choice is stable.
 */
export function findInteractionTarget(
  objects: readonly InteractiveObject[],
  pos: TilePos,
  facing: Direction,
): InteractiveObject | undefined {
  const delta = DIRECTION_DELTAS[facing];
  const ahead = { x: pos.x + delta.x, y: pos.y + delta.y };
  let best: { item: InteractiveObject; distance: number; faced: boolean } | undefined;
  for (const item of objects) {
    const distance = chebyshevToRect(pos, item.object.footprint);
    if (distance > item.range) continue;
    const faced = chebyshevToRect(ahead, item.object.footprint) === 0;
    const better =
      !best ||
      distance < best.distance ||
      (distance === best.distance && faced && !best.faced) ||
      (distance === best.distance && faced === best.faced && item.object.id < best.item.object.id);
    if (better) best = { item, distance, faced };
  }
  return best?.item;
}
